import { getCurrentInstance } from 'vue';
import { useDealStore } from '@/store/dealStore';
import { useUIStore } from '@/store/uiStore';
import { useToastStore } from '@/store/toastStore';

/**
 * Delete a deal, or explain why it can't be deleted when a project hangs off it.
 * `onOpenProject(projectId)` is offered from the blocked dialog.
 */
export function useDealDelete({ onDeleted, onOpenProject } = {}) {
  const instance = getCurrentInstance();
  const $t = instance.appContext.config.globalProperties.$t;
  const dealStore = useDealStore();
  const uiStore = useUIStore();
  const toastStore = useToastStore();

  const showBlocked = (project) => {
    const description = project?.project_number
      ? $t('deals.delete_blocked_has_project_named', { project: project.project_number })
      : $t('deals.delete_blocked_has_projects');

    const modalId = uiStore.addModal(null, {
      title: $t('deals.delete_blocked_title'),
      description,
      ...(project?.id && onOpenProject
        ? {
            submitText: $t('projects.view_project'),
            cancelText: $t('common.close'),
            onSubmit: () => {
              uiStore.closeModal(modalId);
              onOpenProject(project.id);
            },
            onCancel: () => uiStore.closeModal(modalId),
          }
        : {}),
    });
  };

  const isBlockedByProjects = (error) => {
    const code = error?.response?.data?.code;
    const apiError = error?.response?.data?.error || '';
    return code === 'DEAL_HAS_PROJECTS' || apiError.includes('existing projects');
  };

  /**
   * @param {string} dealId
   * @param {Object|null} project - linked project when already known; skips the confirm step
   */
  const deleteDeal = (dealId, project = null) => {
    if (project) {
      showBlocked(project);
      return;
    }

    const modalId = uiStore.addModal(null, {
      title: $t('deals.delete_deal'),
      description: $t('deals.delete_confirm'),
      submitText: $t('common.delete'),
      status: 'danger',
      onSubmit: async () => {
        uiStore.updateModalProps(modalId, { loading: true });
        try {
          await dealStore.deleteDeal(dealId);
          uiStore.closeModal(modalId);
          toastStore.addToast({ type: 'success', message: $t('deals.deal_deleted') });
          onDeleted?.();
        } catch (error) {
          if (isBlockedByProjects(error)) {
            uiStore.closeModal(modalId);
            showBlocked(error.response.data.projects?.[0] || null);
            return;
          }
          const message = error?.response?.data?.error || $t('deals.delete_failed');
          uiStore.updateModalProps(modalId, { loading: false, description: message });
          toastStore.addToast({ type: 'error', message });
        }
      },
    });
  };

  return { deleteDeal };
}
